import { useEffect, useRef } from 'react';
import { useStateQueryStringToNumber } from '../../hooks/use-query-string-state';
import { useErrorContext } from '../error-context/index';
import { usePersistenceContext } from '../persistence-context/index';
import { useResponsiveDrawerContext } from '../responsive-drawer/index';

export enum WizardValidation {
	Invalid,
	Valid,
	Disabled,
	Suppressed
}

export type WizardStep<T = unknown> = {
	id?: string;
	title: string;
	component: (props: StepComponentProps) => JSX.Element;
	fieldIds?: string[];
	/**Validation that will not use the zod schema, if present any potential schemas will be ignored and only this validation function will be used */
	//TODO: Deprecate the boolean type and only use WizardValidation for validation
	validation?: (data: T) => boolean | WizardValidation;
	buttonsLeft?: React.ComponentType<ButtonsLeftProps>;
	buttonsRight?: React.ComponentType<ButtonsRightProps>;
};

export type ButtonsLeftProps = { handlePrevStep?: ReturnType<typeof useWizard>['handlePrevStep'] };
export type ButtonsRightProps = {
	handleNextStep?: ReturnType<typeof useWizard>['handleNextStep'];
	allStepsValid?: boolean;
	step: number;
	stepValidations: Array<WizardValidation>;
	visible?: boolean;
};

export type StepComponentProps = {
	currentStep: number;
	nextStep: ReturnType<typeof useWizard>['handleNextStep'];
	stepVisited: ReturnType<typeof useWizard>['stepVisited'];
};

export const WIZARD_STEP_PARAM = 'step';

export type WizardOptions = { lock?: boolean; stepKey?: string };
export function useWizard<T extends object = {}>(steps: WizardStep<T>[], options?: WizardOptions) {
	const inDrawer = !!useResponsiveDrawerContext();

	const key = options?.stepKey ?? (inDrawer ? 'ds' : WIZARD_STEP_PARAM);
	const [step, setStep] = useStateQueryStringToNumber(
		key,
		0,
		steps.reduce((acc, curr, idx) => {
			if (curr.id) acc[curr.id] = idx;
			return acc;
		}, {})
	);
	const visitedRef = useRef<Record<number, boolean>>({});

	const { data } = usePersistenceContext<T>();

	const stepData = data();

	const { setTouched, validateFields } = useErrorContext() ?? {};

	const handleChangeStep = (ns: number) => {
		if (!options?.lock) {
			setStep(ns);
			visitedRef.current[ns] = true;
		}
	};

	useEffect(() => {
		if (!inDrawer) return;
		return () => {
			// if we're in a drawer, we want to delete the step state
			const search = new URLSearchParams(window.location.search);

			search.delete(key);
			const qs = search.toString();
			if (window.location.search !== qs) window.history.pushState(null, '', window.location.pathname + '?' + qs);
		};
	}, [inDrawer]);

	const CurrentStepComponent = steps[step].component;
	const ButtonsLeft = steps[step].buttonsLeft;
	const ButtonsRight = steps[step].buttonsRight;

	const stepValidations = steps.map((st, idx) => {
		let valid: WizardValidation | boolean = WizardValidation.Valid;
		if (st.validation) {
			valid = st.validation?.(stepData);
		} else if (st.fieldIds) {
			valid = validateFields(st.fieldIds);
			if (idx < step) {
				setTouched(st.fieldIds);
			}
		}
		const currentValidStep =
			typeof valid === 'boolean' ? (valid ? WizardValidation.Valid : WizardValidation.Invalid) : (valid ?? WizardValidation.Valid);
		return currentValidStep;
	});

	const handleNextStep = () => {
		for (let next = step + 1; next < steps.length; next++) {
			if (stepValidations[next] !== WizardValidation.Suppressed) {
				handleChangeStep(next);
				break;
			}
		}
	};

	const handlePrevStep = () => {
		for (let prev = step - 1; prev >= 0; prev--) {
			if (stepValidations[prev] !== WizardValidation.Suppressed) {
				handleChangeStep(prev);
				break;
			}
		}
	};

	const stepIsVisited = (ns: number) => visitedRef.current[ns];

	let visitedArr = [];
	for (let st = step + 1; st < steps.length; st++) {
		visitedArr.push(stepIsVisited(st));
	}

	const stepVisited = visitedArr.some(s => s === true);

	return {
		step,
		CurrentStepComponent,
		ButtonsLeft,
		ButtonsRight,
		handleChangeStep,
		handleNextStep,
		handlePrevStep,
		stepVisited,
		stepValidations,
		data: stepData
	};
}
